import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Flame, ArrowRight, ArrowLeft, Check, Dumbbell, Activity, Calendar as CalendarIcon, Clock, AlertCircle, Wrench, Heart, Building2, CalendarDays, ShieldCheck, Layers, Sparkles } from 'lucide-react';

import { useProfile } from '../context/ProfileContext';
import { equipmentCategories, allEquipmentIds, injuryRegions, injurySystemic, goalOptions, experienceLevels } from '../data/equipment';
import { recoveryActivities } from '../data/endurance';
import { programs, localISO, parseLocalISO } from '../data/programs';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { smartPresetForProfile, smartModulesForProfile } from '../data/displayPrefs';
import { ADAPTIVE_SUBMODES } from '../data/adaptiveSubmodes';
import ModeAestheticPreview from '../components/ModeAestheticPreview';

// Unified onboarding flow — 9 steps. Quickstart (route: /onboarding) covers
// steps 1–4; Continuation (route: /onboarding/full) covers steps 5–9.
// Step numbering is shared across both halves so users see "Step N of 9"
// progress regardless of which half they're in.
//
// Merged steps (vs. original 13-step flow):
//   experience_goals  ← experience + goals + AI notice
//   training_structure ← commitment + program + startDate
export const STEPS = [
  { id: 'mode', label: 'Mode', icon: ShieldCheck },                     // 1 — Quickstart
  { id: 'experience_goals', label: 'Experience & Goals', icon: Heart }, // 2 — Quickstart
  { id: 'equipment', label: 'Equipment', icon: Wrench },                // 3 — Quickstart
  { id: 'display', label: 'Display', icon: Layers },                    // 4 — Quickstart
  { id: 'basics', label: 'You', icon: Activity },                       // 5 — Continuation
  { id: 'injuries', label: 'Limitations', icon: AlertCircle },          // 6 — Continuation
  { id: 'training_structure', label: 'Structure', icon: Clock },        // 7 — Continuation
  { id: 'recovery', label: 'Endurance', icon: CalendarIcon },           // 8 — Continuation
  { id: 'review', label: 'Review', icon: Check },                       // 9 — Continuation
];

export const TOTAL_ONBOARDING_STEPS = STEPS.length;

const Pill = ({ active, children, onClick, disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled}
    className={`px-4 py-2 text-sm rounded-md border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
      active ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary' : 'bg-enf-surface-deep border-enf-surface-border text-enf-text-secondary hover:border-enf-surface-border hover:text-enf-text-primary'
    }`}>{children}</button>
);

const Card = ({ active, onClick, children, className = '' }) => (
  <button type="button" onClick={onClick}
    className={`text-left p-5 rounded-xl border transition-all ${
      active ? 'bg-enf-accent/10 border-enf-accent-fg' : 'bg-enf-surface-deep border-enf-surface-border hover:border-enf-surface-border'
    } ${className}`}>{children}</button>
);

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2 block">{label}</label>
    {children}
  </div>
);

const input = 'w-full px-4 py-3 bg-enf-surface-deep border border-enf-surface-border rounded-md text-enf-text-primary placeholder:text-enf-text-muted focus:outline-none focus:border-enf-accent-fg transition-colors';

const ONBOARDING_DRAFT_KEY = 'enferno_onboarding_draft_v1';

// Single source of truth for step completion: a step is "done" only if the
// user has explicitly walked through it. We track this in
// `profile.onboardingStepsAnswered`. Default profile values
// (daysPerWeek=4, programType='upper-lower', adaptiveMode=false, etc.) no
// longer masquerade as user choices. Field checks are intentionally NOT used
// here — they were the root cause of bad question-count math.
export const isStepComplete = (stepId, profile) => {
  if (!profile) return false;

  // Legacy backfill: users who completed onboarding BEFORE the
  // `onboardingStepsAnswered` field existed get treated as fully done — they
  // already gave us all the data, no point re-asking. Detection: their
  // profile has completedOnboarding=true but lacks the new array entirely.
  if (profile.completedOnboarding === true && !Array.isArray(profile.onboardingStepsAnswered)) {
    return true;
  }

  const answered = Array.isArray(profile.onboardingStepsAnswered) ? profile.onboardingStepsAnswered : [];
  if (answered.includes(stepId)) return true;

  // Legacy compat for merged step IDs: users who completed onboarding with
  // the old 13-step flow have constituent IDs in their answered array instead
  // of the merged IDs. Count the merged step as done if ALL its parts are.
  if (stepId === 'experience_goals') return answered.includes('experience') && answered.includes('goals');
  if (stepId === 'training_structure') return answered.includes('commitment') && answered.includes('program') && answered.includes('startDate');

  return false;
};

/** Injuries/limitations are "known" once the user walked the onboarding step
 *  OR explicitly flagged none / picked a limitation (any storage shape). */
export const isInjuriesProfileComplete = (profile) => {
  if (!profile) return false;
  if (isStepComplete('injuries', profile)) return true;
  if (profile.noInjuries === true) return true;
  if (profile.injuries && typeof profile.injuries === 'object' && Object.values(profile.injuries).some(Boolean)) {
    return true;
  }
  if (Array.isArray(profile.injuryRegions) && profile.injuryRegions.length > 0) return true;
  if (Array.isArray(profile.injurySystemic) && profile.injurySystemic.length > 0) return true;
  return false;
};

// First step the user hasn't completed yet. Falls back to the last step
// (review) if everything is done — so Edit Profile entry on a fully-onboarded
// user lands on the review page (which is the right "edit anything" surface).
export const firstIncompleteStep = (profile) => {
  for (let i = 0; i < STEPS.length; i += 1) {
    if (!isStepComplete(STEPS[i].id, profile)) return i;
  }
  return STEPS.length - 1;
};

const Onboarding = ({ scope = 'full' }) => {
  const { profile, update } = useProfile();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const today = localISO(new Date());
  // One-shot init guard — prevents the field-aware skip effect from running
  // again after the user starts navigating (which would yank them back).
  const initializedRef = useRef(false);

  // Quickstart vs Full: in quickstart scope we only walk steps 1-4 (mode,
  // experience+goals, equipment, display) and present a narrow centered
  // card with progress dots + a Skip button. In full scope we walk all 9
  // with a wider layout + progress bar. STEPS export is unchanged so
  // ProfileDepthNudge keeps its source-of-truth.
  const isQuickstart = scope === 'quickstart';
  const visibleSteps = isQuickstart ? STEPS.slice(0, 4) : STEPS;

  // Hydrate draft from localStorage (if user closed the tab mid-flow) → fall back to profile.
  const [draft, setDraft] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(ONBOARDING_DRAFT_KEY) || 'null');
      if (saved && saved.draft) {
        return { ...profile, ...saved.draft, startDate: saved.draft.startDate || profile.startDate || today };
      }
    } catch { /* ignore */ }
    // No stale draft. When completedOnboarding is false (new user or after a Reset
    // Profile), clear any onboardingStepsAnswered that may have been restored by
    // the cloud-sync 409 conflict handler — without this, the step-skip loop in
    // next() would fast-forward through all steps and immediately finalize.
    const base = { ...profile, startDate: profile.startDate || today };
    if (!profile.completedOnboarding) {
      base.onboardingStepsAnswered = [];
    }
    return base;
  });

  // Field-aware start step. Waits for `profile` to actually be hydrated from
  // backend before running — useEffect([]) on mount would race with profile
  // load and lock the user onto step 0 even when later data arrives.
  // Detection: `completedOnboarding` is undefined ONLY while profile is still
  // hydrating from the network. Once present (true OR false), we have the
  // real profile and can compute the right start step.
  // Hash takes precedence — direct deep-link from ProfileCompletenessMeter
  // (e.g. /onboarding/full#injuries) jumps straight to that step.
  useEffect(() => {
    if (initializedRef.current) return;
    // Wait for profile hydration. Default profile in context has
    // `completedOnboarding: false`, so this check passes after first
    // ProfileContext render — it specifically guards against the
    // brief "totally empty object" window during initial app boot.
    if (profile?.completedOnboarding === undefined) return;

    const hash = (typeof window !== 'undefined' ? window.location.hash.replace('#', '') : '');
    const hashStepIdx = hash ? visibleSteps.findIndex((s) => s.id === hash) : -1;
    if (hashStepIdx >= 0) {
      setStep(hashStepIdx);
      initializedRef.current = true;
      return;
    }
    try {
      const saved = JSON.parse(localStorage.getItem(ONBOARDING_DRAFT_KEY) || 'null');
      if (saved && typeof saved.step === 'number') {
        const clamped = Math.max(0, Math.min(saved.step, visibleSteps.length - 1));
        // Resume mid-flow only when the saved step is still incomplete.
        // Stale quickstart drafts (e.g. step 4 = Display) must not override
        // firstIncompleteStep when the user opens /onboarding/full to continue.
        if (!isStepComplete(visibleSteps[clamped].id, profile)) {
          setStep(clamped);
          initializedRef.current = true;
          return;
        }
      }
    } catch { /* ignore */ }
    // Field-aware: jump straight to first incomplete step. If everything is
    // already done (returning user who completed all steps in scope),
    // navigate away entirely instead of mounting an empty Review page.
    // firstIncompleteStep operates on STEPS; clamp to visibleSteps for
    // quickstart scope so we never land beyond step 5.
    const firstIdx = Math.min(firstIncompleteStep(profile), visibleSteps.length - 1);
    if (isStepComplete(visibleSteps[firstIdx].id, profile)) {
      if (profile.completedOnboarding) {
        // User has fully onboarded and every visible step is done — bounce to Dashboard.
        initializedRef.current = true;
        navigate('/dashboard', { replace: true });
        return;
      }
      // completedOnboarding is false but steps appear answered — this can happen
      // after a profile reset where the cloud-sync 409 conflict handler restores
      // onboardingStepsAnswered before this effect re-fires. Start from step 0
      // so the user can redo their setup.
      setStep(0);
      initializedRef.current = true;
      return;
    }
    setStep(firstIdx);
    initializedRef.current = true;
  }, [profile, navigate, visibleSteps]);

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  // Persist draft on every change so a closed tab doesn't lose progress.
  useEffect(() => {
    try {
      localStorage.setItem(ONBOARDING_DRAFT_KEY, JSON.stringify({ draft, step, ts: Date.now() }));
    } catch { /* ignore quota */ }
  }, [draft, step]);

  // Tester shortcut: ?skip=ironbridge OR ?skip=tester to jump straight to dashboard
  // (useful for board / consultant demos so they don't slog through the 3-min flow).
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const skip = params.get('skip');
      if (skip === 'ironbridge' || skip === 'tester') {
        // Apply minimal viable defaults so Dashboard works, then jump.
        update({
          ...draft,
          name: draft.name || 'Tester',
          experience: draft.experience || 'intermediate',
          goals: draft.goals?.length ? draft.goals : ['hypertrophy'],
          fullGym: draft.fullGym ?? true,
          startDate: draft.startDate || today,
          completedOnboarding: true,
        });
        try { localStorage.removeItem(ONBOARDING_DRAFT_KEY); } catch { /* ignore */ }
        navigate('/progress', { replace: true });
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stepKey = visibleSteps[step].id;

  const canNext = () => {
    if (stepKey === 'basics') return draft.name && draft.age && draft.sex && draft.heightFt && draft.weight;
    if (stepKey === 'experience_goals') return !!draft.experience && draft.goals.length > 0;
    if (stepKey === 'equipment') return draft.fullGym || draft.noEquipment || draft.equipment.length > 0;
    return true;
  };

  // Finalizer — extracted so it can be called from both "user hit Continue on
  // the last step" AND "user finished their last incomplete continuation step
  // and review is already complete (returning user)" paths.
  // Also called by Skip (quickstart scope only).
  const finalizeOnboarding = (latestDraft = draft, opts = {}) => {
    const userPickedPreset = latestDraft.displayPrefs?.preset;
    const preset = userPickedPreset || smartPresetForProfile(latestDraft);
    const modules = smartModulesForProfile({ ...latestDraft, displayPrefs: { preset } });
    update({
      ...latestDraft,
      completedOnboarding: true,
      // onboardingMode tags how the user finished — 'quickstart' (4 of 4 walked),
      // 'quickstart-skipped' (bailed mid-quickstart), or 'full' (9 of 9 walked).
      // Used by analytics + by ProfileDepthNudge to know whether the user
      // intentionally bailed or completed.
      onboardingMode: opts.mode || (isQuickstart ? 'quickstart' : 'full'),
      onboardingCompletedAt: latestDraft.onboardingCompletedAt || new Date().toISOString(),
      displayPrefs: {
        ...(latestDraft.displayPrefs || {}),
        preset,
        modules: { ...(latestDraft.displayPrefs?.modules || {}), ...modules },
      },
    });
    try { localStorage.removeItem(ONBOARDING_DRAFT_KEY); } catch { /* ignore */ }
    navigate('/progress');
  };

  // Push the current step id into `onboardingStepsAnswered` so isStepComplete
  // can distinguish "user walked through this" from "profile had a default."
  const markAnswered = (d, id) => {
    const cur = Array.isArray(d.onboardingStepsAnswered) ? d.onboardingStepsAnswered : [];
    if (cur.includes(id)) return d;
    return { ...d, onboardingStepsAnswered: [...cur, id] };
  };

  const next = () => {
    const currentId = visibleSteps[step].id;
    const draftWithAnswered = markAnswered(draft, currentId);
    setDraft(draftWithAnswered);

    if (step < visibleSteps.length - 1) {
      // Persist current step's data right now so subsequent skip logic sees
      // the freshly-completed field. (Without this, the next-step lookup
      // uses the stale profile and would skip nothing.)
      update(draftWithAnswered);
      // Hop forward past any already-completed steps — including past the
      // final step if it's already considered complete (returning users
      // whose profile.completedOnboarding === true don't need to re-review).
      // When the search runs off the end of visibleSteps, finalize directly
      // so we don't render an empty step to immediately exit it.
      const mergedProfile = { ...profile, ...draftWithAnswered };
      let nextStep = step + 1;
      while (nextStep < visibleSteps.length && isStepComplete(visibleSteps[nextStep].id, mergedProfile)) {
        nextStep += 1;
      }
      if (nextStep >= visibleSteps.length) {
        finalizeOnboarding(draftWithAnswered);
        return;
      }
      setStep(nextStep);
    } else {
      finalizeOnboarding(draftWithAnswered);
    }
  };

  // Skip (quickstart scope only) — persists whatever steps the user has
  // already Next'd through but DOES NOT credit the current step. Matches
  // the old Quickstart.jsx Skip behavior.
  const skipQuickstart = () => {
    finalizeOnboarding(draft, { mode: 'quickstart-skipped' });
  };
  // Back goes to the immediate previous step (no skip-past-completed logic).
  // Users may want to revisit any prior answer; that's more valuable than a
  // "clean" hop. Pre-Feb-2026 behavior hopped past completed steps which
  // could trap the user on the same screen.
  const back = () => setStep((s) => Math.max(0, s - 1));

  const toggleArr = (key, val) => set({ [key]: draft[key].includes(val) ? draft[key].filter((x) => x !== val) : [...draft[key], val] });
  // Toggling any injury auto-clears the "noInjuries" sentinel so they're mutually exclusive.
  const toggleInj = (id) => set({
    injuries: { ...draft.injuries, [id]: !draft.injuries[id] },
    noInjuries: false,
  });

  const pct = ((step + 1) / visibleSteps.length) * 100;

  // Quickstart: narrow centered card with progress dots + an inline wordmark
  // (the navbar is hidden via hideChrome for /onboarding). Full: wider layout
  // under the existing navbar, with a thin progress bar.
  return (
    <div className={isQuickstart
      ? 'min-h-screen bg-transparent text-enf-text-primary px-4 py-10 sm:py-16'
      : 'min-h-screen bg-transparent text-enf-text-primary pt-20 pb-16'}>
      <div className={isQuickstart ? 'max-w-2xl mx-auto' : 'max-w-4xl mx-auto px-6'}>
        {isQuickstart && (
          <div className="mb-4">
            <span className="enf-brand-pill inline-flex items-center">
              <img
                src="/brand/enferno-wordmark-nav@2x.png"
                srcSet="/brand/enferno-wordmark-nav.png 1x, /brand/enferno-wordmark-nav@2x.png 2x"
                alt="Enferno"
                className="h-8 w-auto"
                loading="eager"
                decoding="async"
              />
            </span>
          </div>
        )}

        {/* Header — dots for quickstart, bar for full */}
        {isQuickstart ? (
          <div className="flex items-center justify-between mb-6">
            <div className="flex gap-1.5">
              {visibleSteps.map((_, i) => (
                <div
                  key={i}
                  className={`h-1 w-12 rounded-full transition-colors ${
                    i <= step ? 'bg-enf-accent' : 'bg-enf-surface-elevated'
                  }`}
                />
              ))}
            </div>
            <span className="text-xs text-enf-text-muted uppercase tracking-widest">{step + 1} of {visibleSteps.length}</span>
          </div>
        ) : (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3 text-xs text-enf-text-muted">
              <span className="uppercase tracking-[0.3em] font-semibold">Step {step + 1} of {visibleSteps.length}</span>
              <span className="font-mono">{visibleSteps[step].label}</span>
            </div>
            <div className="h-1 bg-enf-surface-mid rounded-full overflow-hidden">
              <div className="h-full bg-enf-accent transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}

        <div className="min-h-[480px]">
          {stepKey === 'mode' && (
            <div data-testid="onboarding-mode-step">
              <h2 className="font-display text-4xl md:text-5xl tracking-wide leading-none mb-2">Training Mode</h2>
              <p className="text-enf-text-secondary mb-8 max-w-2xl">
                Two modes — pick whichever fits. You can switch any time.
              </p>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <button
                  type="button"
                  onClick={() => set({ adaptiveMode: false, adaptiveSubmodes: [] })}
                  data-testid="mode-athlete"
                  className={`text-left p-5 rounded-xl border transition-all ${
                    !draft.adaptiveMode ? 'bg-enf-accent/10 border-enf-accent-fg' : 'bg-enf-surface-deep border-enf-surface-border hover:border-enf-surface-border'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Flame className="w-5 h-5 text-enf-accent-fg scale-x-[-1]" />
                      <div className="font-display text-2xl tracking-wide">Athlete Mode</div>
                    </div>
                    {!draft.adaptiveMode && <Check className="w-4 h-4 text-enf-accent-fg" />}
                  </div>
                  <p className="text-sm text-enf-text-secondary mb-4">
                    The default. Multi-discipline athlete — lifts, runs, rides, martial arts. Intensity-positive coaching.
                  </p>
                  <ModeAestheticPreview variant="athlete" />
                </button>

                <button
                  type="button"
                  onClick={() => set({ adaptiveMode: true })}
                  data-testid="mode-adaptive"
                  className={`text-left p-5 rounded-xl border transition-all ${
                    draft.adaptiveMode ? 'bg-adaptive-accent/10 border-adaptive-accent' : 'bg-enf-surface-deep border-enf-surface-border hover:border-enf-surface-border'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-5 h-5 text-adaptive-accent" />
                      <div className="font-display text-2xl tracking-wide">Adaptive Mode</div>
                    </div>
                    {draft.adaptiveMode && <Check className="w-4 h-4 text-adaptive-accent" />}
                  </div>
                  <p className="text-sm text-enf-text-secondary mb-4">
                    Body in a contested state — chronic pain, amputee, post-surgical. Pacing-aware, flare-aware, anti-shame coaching. Perimenopause &amp; older-adult support coming.
                  </p>
                  <ModeAestheticPreview variant="adaptive" />
                </button>
              </div>

              {draft.adaptiveMode && (
                <div className="p-5 rounded-xl border border-adaptive-accent/30 bg-adaptive-accent/5">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-adaptive-accent font-bold mb-2">Tell us more — multi-select OK</div>
                  <p className="text-sm text-enf-text-secondary mb-4">
                    Pick everything that applies. We'll tune programming, copy, and the Pacing Engine to what you need.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {ADAPTIVE_SUBMODES.map((sm) => {
                      const active = (draft.adaptiveSubmodes || []).includes(sm.id);
                      return (
                        <button
                          key={sm.id}
                          type="button"
                          onClick={() => toggleArr('adaptiveSubmodes', sm.id)}
                          data-testid={`adaptive-submode-${sm.id}`}
                          className={`text-left p-3 rounded-md border transition-colors ${
                            active ? 'bg-adaptive-accent/15 border-adaptive-accent text-enf-text-primary' : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:border-enf-surface-border'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="font-semibold text-sm">{sm.label}</div>
                            {active && <Check className="w-3.5 h-3.5 text-adaptive-accent flex-shrink-0 mt-0.5" />}
                          </div>
                          <div className="text-xs text-enf-text-muted mt-0.5">{sm.blurb}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* `welcome` step removed — see STEPS array comment. */}

          {stepKey === 'basics' && (
            <div>
              <h2 className="font-display text-4xl md:text-5xl tracking-wide leading-none mb-2">About You</h2>
              <p className="text-enf-text-secondary mb-8">Used to calibrate workout intensity and intake estimates.</p>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Name"><input className={input} value={draft.name} onChange={(e) => set({ name: e.target.value })} placeholder="First name" /></Field>
                <Field label="Age"><input type="number" className={input} value={draft.age} onChange={(e) => set({ age: e.target.value })} placeholder="30" /></Field>
                <Field label="Sex (for biometric estimates)">
                  <div className="flex gap-2">{['Male', 'Female', 'Prefer not to say'].map((s) => (<Pill key={s} active={draft.sex === s} onClick={() => set({ sex: s })}>{s}</Pill>))}</div>
                </Field>
                <Field label="Weight (lbs)"><input type="number" className={input} value={draft.weight} onChange={(e) => set({ weight: e.target.value })} placeholder="180" /></Field>
                <Field label="Height">
                  <div className="grid grid-cols-2 gap-2">
                    <input type="number" className={input} value={draft.heightFt} onChange={(e) => set({ heightFt: e.target.value })} placeholder="5 ft" />
                    <input type="number" className={input} value={draft.heightIn} onChange={(e) => set({ heightIn: e.target.value })} placeholder="10 in" />
                  </div>
                </Field>
              </div>
              <div className="mt-6">
                <Field label="Do you want to track nutrition?">
                  <div className="flex gap-2">
                    <Pill active={draft.wantsNutrition === true} onClick={() => set({ wantsNutrition: true })}>Yes</Pill>
                    <Pill active={draft.wantsNutrition === false} onClick={() => set({ wantsNutrition: false })}>No</Pill>
                  </div>
                  <p className="text-xs text-enf-text-muted mt-2">Calorie + macro tracking. You can turn this on or off any time from Display preferences.</p>
                </Field>
              </div>
            </div>
          )}

          {stepKey === 'experience_goals' && (
            <div>
              <h2 className="font-display text-4xl md:text-5xl tracking-wide leading-none mb-2">Experience &amp; Goals</h2>
              <p className="text-enf-text-secondary mb-8">Set the foundation — we'll calibrate exercises, progressions, and coaching to match.</p>

              {/* Experience Level */}
              <div className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-3">Experience Level</div>
              <div className="grid gap-3 mb-8">
                {experienceLevels.map((l) => (
                  <Card key={l.id} active={draft.experience === l.id} onClick={() => set({ experience: l.id })}>
                    <div className="flex items-start justify-between mb-1">
                      <div className="text-xl font-bold">{l.name}</div>
                      {draft.experience === l.id && <Check className="w-4 h-4 text-enf-accent-fg flex-shrink-0" />}
                    </div>
                    <div className="text-sm text-enf-text-secondary">{l.desc}</div>
                  </Card>
                ))}
              </div>

              {/* Training Goals */}
              <div className="pt-6 border-t border-enf-surface-border mb-8">
                <div className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Training Goals</div>
                <p className="text-enf-text-secondary text-sm mb-4">Pick up to three. We'll prioritize your top goal.</p>
                <div className="grid md:grid-cols-2 gap-3">
                  {goalOptions.map((g) => (
                    <Card key={g.id} active={draft.goals.includes(g.id)} onClick={() => toggleArr('goals', g.id)}>
                      <div className="flex items-start justify-between mb-1">
                        <div className="text-lg font-bold">{g.name}</div>
                        {draft.goals.includes(g.id) && <Check className="w-4 h-4 text-enf-accent-fg flex-shrink-0" />}
                      </div>
                      <div className="text-sm text-enf-text-secondary">{g.desc}</div>
                    </Card>
                  ))}
                </div>
              </div>

              {/* AI Coach notice */}
              <div className="pt-6 border-t border-enf-surface-border">
                <div className="flex items-start gap-3 p-4 bg-enf-surface-elevated border border-enf-surface-border rounded-xl">
                  <Sparkles className="w-4 h-4 text-enf-accent-fg mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-enf-text-secondary leading-relaxed">
                    No matter your experience or what you're here to accomplish, your AI Coach will support you. Control how Enferno personalizes your AI experience in{' '}
                    <strong className="text-enf-text-primary">Profile → Your Data &amp; Consents</strong>.
                  </p>
                </div>
              </div>
            </div>
          )}

          {stepKey === 'injuries' && (
            <div>
              <h2 className="font-display text-4xl md:text-5xl tracking-wide leading-none mb-2">Injuries or Limitations</h2>
              <p className="text-enf-text-secondary mb-8">Enferno will substitute or avoid exercises that aggravate flagged areas. Check all that apply — or tell us you're injury-free.</p>

              {/* Mutually-exclusive "None" sentinel — picking it clears any selected injuries.
                  Picking any specific injury below auto-clears this sentinel via toggleInj. */}
              <button
                type="button"
                onClick={() => set({ noInjuries: true, injuries: {}, injuryNotes: '' })}
                data-testid="injury-none-pill"
                className={`w-full mb-5 p-3 rounded-md border transition-colors text-left flex items-center gap-3 ${
                  draft.noInjuries
                    ? 'bg-emerald-600/10 border-emerald-500/60 text-enf-text-primary'
                    : 'bg-enf-surface-deep border-enf-surface-border text-enf-text-secondary hover:border-enf-surface-border hover:text-enf-text-primary'
                }`}
              >
                <Check className={`w-4 h-4 flex-shrink-0 ${draft.noInjuries ? 'text-emerald-400' : 'text-enf-text-muted'}`} />
                <div>
                  <div className="font-semibold text-sm">None — I'm injury-free</div>
                  <div className="text-xs text-enf-text-muted mt-0.5">No flagged limitations. Coach trains you full-tilt.</div>
                </div>
              </button>

              <Field label="Joint or region">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6">
                  {injuryRegions.map((r) => (
                    <Pill key={r.id} active={!!draft.injuries[r.id]} onClick={() => toggleInj(r.id)}>{r.name}</Pill>
                  ))}
                </div>
              </Field>
              <Field label="Systemic conditions">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-6">
                  {injurySystemic.map((r) => (
                    <Pill key={r.id} active={!!draft.injuries[r.id]} onClick={() => toggleInj(r.id)}>{r.name}</Pill>
                  ))}
                </div>
              </Field>
              <Field label="Anything else? (Optional)">
                <textarea className={`${input} min-h-[80px] resize-none`} value={draft.injuryNotes} onChange={(e) => set({ injuryNotes: e.target.value })}
                  placeholder="e.g., L4 disc bulge, ACL repair 2022, mild scoliosis..." />
              </Field>
            </div>
          )}

          {stepKey === 'equipment' && (
            <div>
              <h2 className="font-display text-4xl md:text-5xl tracking-wide leading-none mb-2">Available Equipment</h2>
              <p className="text-enf-text-secondary mb-6">Select all available equipment. You can update this any time — useful when you travel.</p>

              <div className="grid md:grid-cols-3 gap-3 mb-6">
                <button type="button"
                  onClick={() => set({ fullGym: true, noEquipment: false, equipment: allEquipmentIds, programType: draft.programType === 'calisthenics' ? 'upper-lower' : draft.programType })}
                  className={`text-left p-5 rounded-xl border transition-all ${
                    draft.fullGym ? 'bg-enf-accent/10 border-enf-accent-fg' : 'bg-enf-surface-deep border-enf-surface-border hover:border-enf-surface-border'
                  }`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Building2 className={`w-5 h-5 ${draft.fullGym ? 'text-enf-accent-fg' : 'text-enf-text-secondary'}`} />
                      <div className="font-display text-xl tracking-wide">Full Commercial Gym</div>
                    </div>
                    {draft.fullGym && <Check className="w-4 h-4 text-enf-accent-fg mt-1.5" />}
                  </div>
                  <div className="text-sm text-enf-text-secondary">Access to everything — barbells, racks, machines, cardio.</div>
                </button>

                <button type="button"
                  onClick={() => set({ fullGym: false, noEquipment: false, equipment: draft.fullGym || draft.noEquipment ? [] : draft.equipment, programType: draft.programType === 'calisthenics' ? 'upper-lower' : draft.programType })}
                  className={`text-left p-5 rounded-xl border transition-all ${
                    !draft.fullGym && !draft.noEquipment ? 'bg-enf-accent/10 border-enf-accent-fg' : 'bg-enf-surface-deep border-enf-surface-border hover:border-enf-surface-border'
                  }`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Wrench className={`w-5 h-5 ${!draft.fullGym && !draft.noEquipment ? 'text-enf-accent-fg' : 'text-enf-text-secondary'}`} />
                      <div className="font-display text-xl tracking-wide">Select My Equipment</div>
                    </div>
                    {!draft.fullGym && !draft.noEquipment && <Check className="w-4 h-4 text-enf-accent-fg mt-1.5" />}
                  </div>
                  <div className="text-sm text-enf-text-secondary">Home gym, hotel gym, or limited setup — pick what you have.</div>
                </button>

                <button type="button"
                  onClick={() => set({ fullGym: false, noEquipment: true, equipment: [], programType: 'calisthenics' })}
                  className={`text-left p-5 rounded-xl border transition-all ${
                    draft.noEquipment ? 'bg-enf-accent/10 border-enf-accent-fg' : 'bg-enf-surface-deep border-enf-surface-border hover:border-enf-surface-border'
                  }`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Dumbbell className={`w-5 h-5 ${draft.noEquipment ? 'text-enf-accent-fg' : 'text-enf-text-secondary'}`} />
                      <div className="font-display text-xl tracking-wide">No Equipment</div>
                    </div>
                    {draft.noEquipment && <Check className="w-4 h-4 text-enf-accent-fg mt-1.5" />}
                  </div>
                  <div className="text-sm text-enf-text-secondary">Calisthenics only — body-weight workouts, anywhere.</div>
                </button>
              </div>

              {draft.noEquipment && (
                <div className="p-4 bg-enf-surface-special/40 border border-enf-border-special/30 rounded-md mb-4 text-sm">
                  <div className="text-enf-role-info uppercase tracking-widest text-xs font-semibold mb-1">Calisthenics program activated</div>
                  <p className="text-enf-text-primary">We'll generate bodyweight workouts. Once you finish onboarding we'll suggest a few low-cost essentials (pull-up bar, ab wheel, bands) that 10x your variety.</p>
                </div>
              )}

              {!draft.fullGym && !draft.noEquipment && (
                <div className="space-y-5 pt-2 border-t border-enf-surface-border">
                  <div className="text-xs uppercase tracking-widest text-enf-role-info font-semibold pt-4">Pick everything you have access to</div>
                  {equipmentCategories.map((cat) => (
                    <div key={cat.category}>
                      <div className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-2">{cat.category}</div>
                      <div className="flex flex-wrap gap-2">
                        {cat.items.map((i) => (
                          <Pill key={i.id} active={draft.equipment.includes(i.id)} onClick={() => toggleArr('equipment', i.id)}>{i.name}</Pill>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {stepKey === 'training_structure' && (
            <div>
              <h2 className="font-display text-4xl md:text-5xl tracking-wide leading-none mb-2">Training Structure</h2>
              <p className="text-enf-text-secondary mb-8">Set your weekly commitment, program split, and start date. Change any of these any time from Strength or Profile.</p>

              {/* Commitment */}
              <div className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-3">Weekly Commitment</div>
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div>
                  <Field label="Days per week">
                    <div className="grid grid-cols-7 gap-2">
                      {[1, 2, 3, 4, 5, 6, 7].map((d) => (<Pill key={d} active={draft.daysPerWeek === d} onClick={() => set({ daysPerWeek: d, schedule: null })}>{d}</Pill>))}
                    </div>
                    <p className="text-xs text-enf-text-muted mt-2">Non-training days become Endurance days. Pick your activity + intensity on the day (Easy, Steady, or Sport).</p>
                  </Field>
                </div>
                <div>
                  <Field label="Minutes per session">
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { v: 30, label: '30 min', t: 'Tier 1 — essentials only' },
                        { v: 45, label: '45 min', t: 'Tier 1 + 2 — add accessory' },
                        { v: 60, label: '60 min', t: 'Tier 1 + 2 + 3 — complete' },
                      ].map((o) => (
                        <button key={o.v} type="button" onClick={() => set({ minutesPerSession: o.v })}
                          className={`p-3 rounded-md border text-left transition-colors ${draft.minutesPerSession === o.v ? 'bg-enf-accent/10 border-enf-accent-fg' : 'bg-enf-surface-deep border-enf-surface-border hover:border-enf-surface-border'}`}>
                          <div className="text-base font-bold">{o.label}</div>
                          <div className="text-xs text-enf-text-muted mt-1">{o.t}</div>
                        </button>
                      ))}
                    </div>
                  </Field>
                </div>
              </div>

              {/* Program Split */}
              <div className="pt-6 border-t border-enf-surface-border mb-8">
                <div className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Program Split</div>
                <p className="text-enf-text-secondary text-sm mb-4">
                  {draft.noEquipment
                    ? 'You selected No Equipment, so Enferno runs the Calisthenics program. You can switch any time after onboarding.'
                    : 'This determines how your week is structured. Default is Push / Pull / Legs — change any time from the Strength tab.'}
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {Object.values(programs)
                    .filter((p) => draft.noEquipment ? p.id === 'calisthenics' : p.id !== 'calisthenics')
                    .map((p) => {
                      const active = draft.programType === p.id;
                      const fits = (draft.daysPerWeek || 4) >= p.minDays;
                      return (
                        <button key={p.id} type="button" onClick={() => set({ programType: p.id, schedule: null })}
                          className={`text-left p-4 rounded-xl border transition-all ${
                            active ? 'bg-enf-accent/10 border-enf-accent-fg' : 'bg-enf-surface-deep border-enf-surface-border hover:border-enf-surface-border'
                          }`}>
                          <div className="flex items-center justify-between mb-1">
                            <div className="font-display text-2xl tracking-wide">{p.name}</div>
                            {active && <Check className="w-4 h-4 text-enf-accent-fg" />}
                          </div>
                          <div className="text-sm text-enf-text-secondary mb-2">{p.description}</div>
                          <div className="text-xs text-enf-text-muted">
                            Best for: {p.bestFor}
                            {!fits && <span className="ml-2 text-enf-role-warning">· needs {p.minDays}+ days/wk</span>}
                          </div>
                        </button>
                      );
                    })}
                </div>
              </div>

              {/* Start Date */}
              <div className="pt-6 border-t border-enf-surface-border">
                <div className="text-xs uppercase tracking-widest text-enf-text-muted font-semibold mb-1">Start Date</div>
                <p className="text-enf-text-secondary text-sm mb-4">
                  Whatever day you pick becomes <span className="text-enf-role-info font-semibold">Day 1</span> of your rotation — your program flows from there, not from Monday.
                </p>
                <Popover>
                  <PopoverTrigger asChild>
                    <button type="button"
                      className="inline-flex items-center gap-3 px-5 py-4 bg-enf-surface-deep border border-enf-surface-border hover:border-enf-border-special rounded-xl text-left transition-colors"
                      data-testid="onboarding-startdate-trigger">
                      <CalendarDays className="w-5 h-5 text-enf-role-info" />
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-enf-text-muted font-semibold">Day 1</div>
                        <div className="font-display text-2xl tracking-wide leading-none mt-0.5">
                          {draft.startDate ? format(new Date(draft.startDate + 'T00:00:00'), 'EEEE, MMM d') : 'Pick a date'}
                        </div>
                      </div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-enf-surface-deep border-enf-surface-border" align="start">
                    <Calendar
                      mode="single"
                      selected={draft.startDate ? new Date(draft.startDate + 'T00:00:00') : new Date()}
                      onSelect={(d) => d && set({ startDate: localISO(d) })}
                      disabled={(d) => {
                        const minBack = new Date(); minBack.setDate(minBack.getDate() - 90);
                        return d < minBack;
                      }}
                      initialFocus
                      className="bg-enf-surface-deep text-enf-text-primary"
                    />
                  </PopoverContent>
                </Popover>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button type="button" onClick={() => set({ startDate: today })}
                    className="px-3 py-1.5 text-xs rounded-md border bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:border-enf-role-info hover:text-enf-text-primary transition-colors">
                    Today
                  </button>
                  {[1, 2, 3, 4].map((offset) => {
                    const d = new Date(); d.setDate(d.getDate() + offset);
                    const iso = localISO(d);
                    return (
                      <button key={offset} type="button" onClick={() => set({ startDate: iso })}
                        className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                          draft.startDate === iso ? 'bg-enf-accent border-enf-accent-fg text-enf-text-primary' : 'bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:border-enf-role-info hover:text-enf-text-primary'
                        }`}>
                        {format(d, 'EEE, MMM d')}
                      </button>
                    );
                  })}
                  <button type="button" onClick={() => {
                    const d = new Date(); const dow = d.getDay();
                    const daysUntilMonday = ((1 - dow + 7) % 7) || 7;
                    d.setDate(d.getDate() + daysUntilMonday);
                    set({ startDate: localISO(d) });
                  }} className="px-3 py-1.5 text-xs rounded-md border bg-enf-surface-mid border-enf-surface-border text-enf-text-secondary hover:border-enf-role-info hover:text-enf-text-primary transition-colors">
                    Next Monday
                  </button>
                </div>
                <div className="mt-5 p-4 bg-enf-surface-elevated border border-enf-surface-border rounded-md text-sm text-enf-text-primary leading-relaxed">
                  <strong className="text-enf-role-info">Example:</strong> If you pick Friday and your program is Push / Pull / Legs / Core, Friday = Push, Saturday = Pull, Sunday = Rest, Monday = Legs, and so on.
                </div>
              </div>
            </div>
          )}

          {stepKey === 'recovery' && (
            <div>
              <h2 className="font-display text-4xl md:text-5xl tracking-wide leading-none mb-2">Endurance and Recovery</h2>
              <p className="text-enf-text-secondary mb-8">Walk, ride, climb, swim, row. We'll suggest these on your non-lifting days and let you dial in the intensity — easy active-recovery, steady training, or full sport pace.</p>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {recoveryActivities.map((a) => (
                  <Card key={a.id} active={draft.recoveryActivities.includes(a.id)} onClick={() => toggleArr('recoveryActivities', a.id)}>
                    <div className="flex items-center justify-between">
                      <div className="text-base font-bold">{a.name}</div>
                      {draft.recoveryActivities.includes(a.id) && <Check className="w-4 h-4 text-enf-accent-fg" />}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {stepKey === 'display' && (
            <div>
              <h2 className="font-display text-4xl md:text-5xl tracking-wide leading-none mb-2">Display Preferences</h2>
              <p className="text-enf-text-secondary mb-8">Pick the display mode that matches how you like to use an app. You can change this any time in Profile.</p>
              <div className="grid gap-3">
                {[
                  {
                    id: 'default',
                    label: 'Full',
                    blurb: 'Everything on. All modules in the nav, every dashboard widget, coach tips and motivators.',
                    detail: 'Best if you want the full experience and like discovering features as you go.',
                  },
                  {
                    id: 'simplified',
                    label: 'Simplified',
                    blurb: 'Dashboard trimmed down. Coach tips, motivators, and fuel reminders hidden.',
                    detail: 'Best if you want the core training tools without extra noise.',
                  },
                  {
                    id: 'skeleton',
                    label: 'Skeleton',
                    blurb: 'Only Progress, Strength, Library, and Impact in the nav. Everything else off by default — turn things back on as you need them.',
                    detail: 'Best if you want the minimum to train and log. Endurance, Combatives, Nutrition, and Coach are off until you turn them on.',
                  },
                ].map((p) => {
                  const active = (draft.displayPrefs?.preset || 'default') === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => set({ displayPrefs: { ...(draft.displayPrefs || {}), preset: p.id } })}
                      className={`text-left p-5 rounded-xl border transition-all ${
                        active ? 'bg-enf-accent/10 border-enf-accent-fg' : 'bg-enf-surface-deep border-enf-surface-border hover:border-enf-surface-border'
                      }`}
                      data-testid={`onboarding-display-${p.id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-display text-2xl tracking-wide leading-none">{p.label}</div>
                        {active && <Check className="w-4 h-4 text-enf-accent-fg" />}
                      </div>
                      <div className="text-sm text-enf-text-secondary mb-1">{p.blurb}</div>
                      <div className="text-xs text-enf-text-muted">{p.detail}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {stepKey === 'review' && (
            <div>
              <h2 className="font-display text-4xl md:text-5xl tracking-wide leading-none mb-2">Ready, {draft.name || 'athlete'}?</h2>
              <p className="text-enf-text-secondary mb-8">Enferno will use this to build every workout. Edit any time in your profile.</p>
              <div className="grid md:grid-cols-2 gap-3">
                {[
                  { l: 'Start date', v: draft.startDate ? format(new Date(draft.startDate + 'T00:00:00'), 'EEEE, MMM d, yyyy') : 'Today' },
                  { l: 'Experience', v: draft.experience || '—' },
                  { l: 'Goals', v: draft.goals.join(', ') || '—' },
                  { l: 'Schedule', v: `${draft.daysPerWeek} days / ${draft.minutesPerSession} min` },
                  { l: 'Program', v: programs[draft.programType]?.name || draft.programType },
                  { l: 'Limitations', v: Object.entries(draft.injuries).filter(([, v]) => v).map(([k]) => k).join(', ') || 'None flagged' },
                  { l: 'Equipment', v: draft.noEquipment ? 'No Equipment (Calisthenics)' : draft.fullGym ? 'Full Commercial Gym' : `${draft.equipment.length} items selected` },
                  { l: 'Endurance', v: `${draft.recoveryActivities.length} activities` },
                ].map((r) => (
                  <div key={r.l} className="bg-enf-surface-deep border border-enf-surface-border rounded-md p-4">
                    <div className="text-xs uppercase tracking-widest text-enf-text-muted">{r.l}</div>
                    <div className="text-sm font-semibold mt-1 capitalize">{r.v}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer nav — Back is shown on every step except step 1 so users
            can revisit any earlier answer. Form values are preserved.
            Quickstart scope also shows "Skip for now" on the far left so
            users can bail without committing to every step. */}
        <div className="flex items-center justify-between mt-10 pt-6 border-t border-enf-surface-border">
          <div className="flex items-center gap-4">
            {isQuickstart && (
              <button
                type="button"
                onClick={skipQuickstart}
                className="text-xs text-enf-text-muted hover:text-enf-text-secondary underline-offset-2 hover:underline transition-colors"
                data-testid="quickstart-skip"
              >
                Skip for now
              </button>
            )}
            {step > 0 && (
              <button onClick={back} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm text-enf-text-secondary hover:text-enf-text-primary transition-colors" data-testid="onboarding-back">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
            )}
          </div>
          <button onClick={next} disabled={!canNext()} className="inline-flex items-center gap-2 px-6 py-3 bg-enf-accent hover:bg-enf-accent-hover text-enf-accent-contrast disabled:bg-enf-surface-mid disabled:text-enf-text-muted disabled:cursor-not-allowed rounded-md font-semibold text-sm transition-colors" data-testid="onboarding-next">
            {step === visibleSteps.length - 1
              ? (isQuickstart ? 'Light the fire' : 'Light it up')
              : 'Continue'} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
